<template>
  <div
    class="dialog-content relative w-full border border-gray-200 bg-white shadow-lg flex flex-col overflow-hidden max-w-[570px] max-h-[90vh] rounded-lg"
    @click.stop
  >
    <div class="p-2 border-b border-gray-100 flex items-center justify-between">
      <h2 class="text-xl font-bold text-gray-900">
        {{ isEditMode ? 'Ubah Kategori Etalase' : 'Tambah Kategori Etalase Baru' }}
      </h2>
      <button
        class="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors"
        @click="$emit('close')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><line
          x1="18"
          y1="6"
          x2="6"
          y2="18"
        /><line
          x1="6"
          y1="6"
          x2="18"
          y2="18"
        /></svg>
      </button>
    </div>

    <div class="p-2 overflow-y-auto flex-1">
      <div class="mb-2">
        <label class="text-[15px] font-medium text-gray-900 block">
          Nama Kategori Etalase<span class="text-red-500">*</span>
        </label>
        <input
          v-model="formName"
          type="text"
          maxlength="50"
          placeholder="Masukkan nama kategori"
          :class="['w-full border px-2 shadow-sm h-[46px] text-[15px] focus:outline-none focus:ring-2 transition-colors rounded-lg', isDuplicate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-white' : 'border-gray-200 focus:border-[#F95E16] focus:ring-[#F95E16]/20 bg-white']"
        >
        <div
          v-if="isDuplicate"
          class="text-[13px] text-red-500 mt-1.5"
        >
          Nama kategori etalase sudah dibuat sebelumnya
        </div>
        <div class="text-[13px] mt-1.5">
          <span :class="formName.length >= 50 ? 'text-red-500' : 'text-gray-400'">{{ formName.length }}/50</span>
        </div>
      </div>

      <div class="mb-2">
        <label class="text-[15px] font-medium text-gray-900 block">Deskripsi</label>
        <textarea
          v-model="formDesc"
          maxlength="200"
          placeholder="Masukkan deskripsi kategori (opsional)"
          class="w-full p-1 border border-gray-200 shadow-sm resize-y min-h-[100px] text-[15px] focus:outline-none focus:ring-2 focus:ring-[#F95E16]/20 focus:border-[#F95E16] transition-colors rounded-lg"
        />
        <div class="text-[13px] text-gray-400">
          {{ formDesc.length }}/200
        </div>
      </div>

      <div>
        <label class="text-lg font-bold text-gray-900 mb-1 block">Pilih Produk</label>
        <p class="text-[15px] text-gray-500 mb-1">
          Pilih produk yang akan masuk ke kategori ini
        </p>

        <div class="relative flex items-center mb-1 border border-gray-200 rounded-lg overflow-hidden h-[46px] shadow-sm">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Cari produk..."
            class="flex-1 px-1 text-[15px] h-[36px] focus:outline-none"
          >
          <div class="h-full border-l border-gray-200 w-[46px] flex items-center justify-center bg-white flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-gray-700"
            ><circle
              cx="11"
              cy="11"
              r="8"
            /><line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            /></svg>
          </div>
        </div>

        <label class="flex items-center space-x-2 mb-3 px-1 cursor-pointer select-none">
          <input
            v-model="isAllSelected"
            type="checkbox"
            class="custom-checkbox flex-shrink-0"
          >
          <span class="text-[14px] font-medium text-gray-800">Pilih Semua</span>
        </label>

        <div class="space-y-2">
          <label
            v-for="product in filteredProducts"
            :key="product.id"
            class="flex items-center gap-3 p-1 bg-[#F9FAFB] cursor-pointer hover:bg-gray-100 transition-colors rounded-lg"
          >
            <input
              v-model="selectedProducts"
              type="checkbox"
              :value="product.id"
              class="custom-checkbox flex-shrink-0"
            >
            <div class="w-10 h-10 rounded-md overflow-hidden bg-white flex-shrink-0 border border-gray-100">
              <img
                :src="product.image || 'https://placehold.co/100x100/F3F4F6/9CA3AF?text=No+Image'"
                :alt="product.name"
                class="w-full h-full object-cover"
                @error="$event.target.src='https://placehold.co/100x100/F3F4F6/9CA3AF?text=No+Image'"
              >
            </div>
            <div class="flex flex-col">
              <p class="text-[13px] font-semibold text-gray-900 leading-tight">{{ product.name }}</p>
              <p class="text-[12px] font-normal text-gray-500 mt-1">
                {{ typeof product.price === 'string' && product.price.toLowerCase().includes('rp') ? product.price : `Rp ${Number(product.price || 0).toLocaleString('id-ID')}` }}
              </p>
            </div>
          </label>
          <div
            v-if="filteredProducts.length === 0"
            class="text-center py-4 text-sm text-gray-500"
          >
            Tidak ada produk yang cocok dengan pencarian
          </div>
        </div>
      </div>
    </div>

    <div class="p-6 pt-4 border-t border-gray-100 flex justify-between gap-3 shrink-0 bg-white">
      <button
        class="inline-flex items-center justify-center rounded-[10px] text-sm transition-colors focus:outline-none bg-[#F3F4F6] font-medium text-gray-700 hover:bg-[#E5E7EB] h-[36px] w-[255px]"
        @click="$emit('close')"
      >
        Batal
      </button>
      <button
        :disabled="!formName || formName.length < 3 || isDuplicate"
        class="inline-flex items-center justify-center rounded-[10px] text-sm transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed bg-[#F95E16] text-white font-bold hover:bg-[#E65113] h-[36px] w-[255px]"
        @click="handleSubmit"
      >
        {{ isEditMode ? 'Simpan Perubahan' : 'Simpan' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryFormDialog',
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    initialName: {
      type: String,
      default: '',
    },
    initialDesc: {
      type: String,
      default: '',
    },
    catalogProducts: {
      type: Array,
      default: () => [],
    },
    initialSelectedProducts: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    activeCategoryId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      formName: this.initialName,
      formDesc: this.initialDesc,
      searchQuery: '',
      selectedProducts: [...this.initialSelectedProducts],
    }
  },
  computed: {
    isDuplicate() {
      return this.categories.some(c => c.name.toLowerCase() === this.formName.trim().toLowerCase() && c.id !== this.activeCategoryId)
    },
    filteredProducts() {
      if (!this.searchQuery) return this.catalogProducts
      return this.catalogProducts.filter(p => p.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
    },
    isAllSelected: {
      get() {
        if (this.filteredProducts.length === 0) return false
        return this.filteredProducts.every(p => this.selectedProducts.includes(p.id))
      },
      set(value) {
        if (value) {
          const filteredIds = this.filteredProducts.map(p => p.id)
          this.selectedProducts = [...new Set([...this.selectedProducts, ...filteredIds])]
        } else {
          const filteredIds = this.filteredProducts.map(p => p.id)
          this.selectedProducts = this.selectedProducts.filter(id => !filteredIds.includes(id))
        }
      },
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submit', {
        newCategoryName: this.formName,
        newCategoryDesc: this.formDesc,
        selectedProducts: this.selectedProducts,
      })
    },
  },
}
</script>

<style scoped>
.custom-checkbox {
  -webkit-appearance: none;
  appearance: none;
  background-color: #ffffff;
  width: 20px;
  height: 20px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  display: inline-block;
  position: relative;
  transition: all 0.2s ease-in-out;
  margin: 0;
}
.custom-checkbox:checked {
  background-color: #F95E16;
  border-color: #F95E16;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
  background-size: 75%;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
